import { supabase } from '../lib/supabase';

export const reviewsService = {
  // Get reviews for a specific user (reviewee)
  async getReviewsForUser(userId) {
    try {
      console.log('Fetching reviews for user:', userId);
      
      // First get the reviews
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('reviewee_id', userId)
        .order('created_at', { ascending: false });

      console.log('Reviews query result:', { reviews, reviewsError });

      if (reviewsError) {
        console.error('Supabase error:', reviewsError);
        throw reviewsError;
      }

      if (!reviews || reviews.length === 0) {
        return [];
      }

      // Get reviewer profiles separately
      const reviewerIds = [...new Set(reviews.map(review => review.reviewer_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, avatar_url')
        .in('id', reviewerIds);

      console.log('Profiles query result:', { profiles, profilesError });

      if (profilesError) {
        console.error('Error fetching reviewer profiles:', profilesError);
      }

      // Combine reviews with profile data
      const reviewsWithProfiles = reviews.map(review => {
        const reviewerProfile = profiles?.find(profile => profile.id === review.reviewer_id);
        return {
          ...review,
          reviewer_profile: reviewerProfile || {
            id: review.reviewer_id,
            name: `User ${review.reviewer_id.slice(0, 8)}`,
            avatar_url: null
          }
        };
      });

      return reviewsWithProfiles;
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
  },

  // Get average rating for a user
  async getAverageRating(userId) {
    try {
      console.log('Fetching average rating for user:', userId);
      
      const { data, error } = await supabase
        .from('reviews')
        .select('rating')
        .eq('reviewee_id', userId);

      console.log('Rating query result:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        return { average: 0, count: 0 };
      }

      const total = data.reduce((sum, review) => sum + review.rating, 0);
      const average = Math.round((total / data.length) * 10) / 10; // Round to 1 decimal place
      
      return { average, count: data.length };
    } catch (error) {
      console.error('Error fetching average rating:', error);
      throw error;
    }
  },

  // Create a new review
  async createReview(reviewData) {
    try {
      console.log('Creating review:', reviewData);
      
      const { data, error } = await supabase
        .from('reviews')
        .insert([reviewData])
        .select()
        .single();

      console.log('Review creation result:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  },

  // Update a review
  async updateReview(reviewId, reviewData) {
    try {
      console.log('Updating review:', reviewId, reviewData);
      
      const { data, error } = await supabase
        .from('reviews')
        .update({
          ...reviewData,
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select()
        .single();

      console.log('Review update result:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  },

  // Delete a review
  async deleteReview(reviewId) {
    try {
      console.log('Deleting review:', reviewId);
      
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      console.log('Review deletion result:', { error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  },

  // Check if user has already reviewed another user for a specific listing
  async hasUserReviewedForListing(reviewerId, revieweeId, listingId) {
    try {
      console.log('Checking if user has reviewed for listing:', { reviewerId, revieweeId, listingId });
      
      const { data, error } = await supabase
        .from('reviews')
        .select('id')
        .eq('reviewer_id', reviewerId)
        .eq('reviewee_id', revieweeId)
        .eq('listing_id', listingId)
        .single();

      console.log('Review check result:', { data, error });

      if (error && error.code === 'PGRST116') {
        // No review found
        return false;
      }
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      return !!data;
    } catch (error) {
      console.error('Error checking review:', error);
      throw error;
    }
  }
};
